import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { roundToPrecision } from '@shared/utils';
import { OptionsSeatControlNames } from '@shared/components/delivery-cargo/components/delivery-cargo-places/delivery-cargo-places.enums';
import { DeliveryPalletsControlNames } from '@shared/components/delivery-pallets/delivery-pallets.enums';
import { CargoTypes } from 'app/services/invoices/invoice-api.enums';

// When the total announced cost (Cost) is raised to equate it to backDelivery/afterpayment sum,
// Cargo, Pallet, and TiresWheels shipments with several places keep their own per-place cost
// that sums up into Cost, so that breakdown must be scaled up by the same coefficient. The
// rounding remainder is retained below to keep the resulting per-place sum equal to Cost.
export function redistributePlacesCost(
  stepDeliveryForm: UntypedFormGroup | null | undefined,
  previousCost: string | number,
  newCost: string | number,
): void {
  if (!stepDeliveryForm) {
    return;
  }

  const cargoType = stepDeliveryForm.get('CargoType')?.value;

  if (cargoType === 'Cargo' && !stepDeliveryForm.get('paramsCommonSeats')?.value) {
    const optionsSeat = stepDeliveryForm.get('OptionsSeat') as UntypedFormArray;
    redistributeCostControls(
      optionsSeat?.controls.map((control) => control.get(OptionsSeatControlNames.Cost)),
      previousCost,
      newCost,
    );
  }

  if (cargoType === 'Pallet') {
    const rowCost = stepDeliveryForm.get(DeliveryPalletsControlNames.RowCost) as UntypedFormArray;
    redistributeCostControls(rowCost?.controls, previousCost, newCost);
  }

  if (cargoType === CargoTypes.TIRES_WHEELS) {
    const tiresWheels = stepDeliveryForm.get('tiresWheels') as UntypedFormArray;
    const populatedCosts = tiresWheels?.controls
      .filter((control) => Number(control.get('count')?.value) > 0)
      .map((control) => control.get('cost'));
    redistributeCostControls(populatedCosts, previousCost, newCost);
  }
}

// Each place's own cost is capped individually (PLACE_COST_INPUT_CONFIG/ROW_COST_INPUT_CONFIG.maxValue),
// but that doesn't stop several places from summing above the shipment's overall max announced cost,
// so this validator re-checks the running total on every place-cost control whenever it changes.
export function totalCostSumValidator(getTotal: () => number, maxValue: number): ValidatorFn {
  return (): ValidationErrors | null => (getTotal() > maxValue ? { totalCostExceeded: true } : null);
}

export function parseCostValue(value: string | number | null | undefined): number {
  return Number(String(value ?? '').replace(',', '.')) || 0;
}

function redistributeCostControls(
  controls: (AbstractControl | null)[] | undefined,
  previousCost: string | number,
  newCost: string | number,
): void {
  const availableControls = controls?.filter((control): control is AbstractControl => Boolean(control)) || [];
  if (availableControls.length < 2) {
    return;
  }

  const newCostNumber = parseCostValue(newCost);
  const previousTotal = parseCostValue(previousCost);
  const ratio = previousTotal ? newCostNumber / previousTotal : null;
  const targetCents = Math.round(newCostNumber * 100);
  const redistributedCents = availableControls.map((control) =>
    Math.round(
      (ratio ? parseCostValue(control.value) * ratio : newCostNumber / availableControls.length) * 100,
    ),
  );
  const roundingDifference = targetCents - redistributedCents.reduce((sum, value) => sum + value, 0);
  if (roundingDifference >= 0) {
    redistributedCents[redistributedCents.length - 1] += roundingDifference;
  } else {
    let centsToRemove = Math.abs(roundingDifference);
    for (let index = redistributedCents.length - 1; index >= 0 && centsToRemove; index -= 1) {
      const removableCents = Math.min(redistributedCents[index], centsToRemove);
      redistributedCents[index] -= removableCents;
      centsToRemove -= removableCents;
    }
  }

  availableControls.forEach((control, index) => {
    const nextValue = redistributedCents[index] / 100;
    control.setValue(nextValue ? roundToPrecision(nextValue, 2) : '', { emitEvent: false });
  });
}
